// src/pages/Inventory.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Pencil, PackagePlus, AlertTriangle, Trash2 } from 'lucide-react';
import { Modal } from './Tables';

const EMPTY = { product_name: '', quantity: 0, low_stock_alert: 5, unit: 'pcs' };
const UNITS = ['pcs', 'bottles', 'packets', 'kg', 'g', 'liters', 'cans', 'boxes'];

// ── Compute low stock purely on the frontend — no reliance on API boolean ──
const isLow = (item) => parseInt(item.quantity) <= parseInt(item.low_stock_alert);

export default function Inventory() {
  const [items,    setItems]    = useState([]);
  const [modal,    setModal]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [restock,  setRestock]  = useState('');
  const [search,   setSearch]   = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/inventory');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error('Failed to load inventory'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd     = () => { setForm(EMPTY); setModal('add'); };
  const openEdit    = (i) => { setSelected(i); setForm({ ...i }); setModal('edit'); };
  const openRestock = (i) => { setSelected(i); setRestock(''); setModal('restock'); };

  const save = async () => {
    if (!form.product_name.trim()) return toast.error('Product name required');
    if (Number(form.quantity) < 0) return toast.error('Quantity cannot be negative');
    try {
      if (modal === 'add') {
        await api.post('/inventory', form);
        toast.success('Product added');
      } else {
        await api.put('/inventory?id=' + selected.id, form);
        toast.success('Product updated');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error saving'); }
  };

  const doRestock = async () => {
    const qty = parseInt(restock);
    if (!qty || qty <= 0) return toast.error('Enter a valid quantity to add');
    try {
      const res = await api.put('/inventory?id=' + selected.id, { action: 'restock', add_quantity: qty });
      toast.success(`Stock updated! New quantity: ${res.data.new_quantity} ${selected.unit}`);
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.product_name}" from inventory?`)) return;
    try {
      await api.delete('/inventory?id=' + item.id);
      toast.success('Item deleted'); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  // ── Derived state ────────────────────────────────────────────────────
  const filtered      = items.filter(i =>
    i.product_name.toLowerCase().includes(search.toLowerCase())
  );
  const lowStockItems = items.filter(isLow);

  // Stock bar width: 0% when qty=0, 100% when qty >= 4× alert threshold
  const stockPct = (item) => {
    const qty   = parseInt(item.quantity)        || 0;
    const alert = parseInt(item.low_stock_alert) || 1;
    const full  = alert * 4;
    return Math.min(100, Math.max(0, Math.round((qty / full) * 100)));
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Inventory</h2>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/> Add Product</button>
      </div>

      {/* ── Low stock alert banner ── */}
      {lowStockItems.length > 0 && (
        <div className="alert alert-warning" style={{ marginBottom: 16, alignItems: 'flex-start' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: 2 }}/>
          <div>
            <strong>⚠️ {lowStockItems.length} item(s) running low on stock!</strong>
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {lowStockItems.map(i => (
                <span key={i.id} className="low-stock-chip">
                  {i.product_name}: <strong>{i.quantity}</strong> {i.unit} left
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Summary stats ── */}
      <div className="inv-stats-row">
        <div className="inv-stat">
          <div className="inv-stat-value">{items.length}</div>
          <div className="inv-stat-label">Total Products</div>
        </div>
        <div className="inv-stat">
          <div className="inv-stat-value" style={{ color: lowStockItems.length > 0 ? '#ef4444' : 'var(--navy)' }}>
            {lowStockItems.length}
          </div>
          <div className="inv-stat-label">Low Stock</div>
        </div>
        <div className="inv-stat">
          <div className="inv-stat-value" style={{ color: '#22c55e' }}>
            {items.filter(i => !isLow(i)).length}
          </div>
          <div className="inv-stat-label">In Stock</div>
        </div>
        <div className="inv-stat">
          <div className="inv-stat-value" style={{ color: 'var(--primary)' }}>
            {items.reduce((a, i) => a + parseInt(i.quantity || 0), 0)}
          </div>
          <div className="inv-stat-label">Total Units</div>
        </div>
      </div>

      {/* ── Search ── */}
      <div style={{ marginBottom: 12 }}>
        <input
          className="search-input"
          placeholder="🔍  Search products…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* ── Table ── */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th style={{ minWidth: 130 }}>Stock Level</th>
              <th>Qty</th>
              <th>Unit</th>
              <th>Alert Below</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(item => {
              const low = isLow(item);
              return (
                <tr key={item.id} style={{ background: low ? '#fef2f2' : '' }}>
                  <td><strong>{item.product_name}</strong></td>
                  <td>
                    <div className="stock-bar-wrap">
                      <div
                        className="stock-bar-fill"
                        style={{
                          width:      stockPct(item) + '%',
                          background: low ? '#ef4444' : stockPct(item) < 40 ? '#f97316' : '#22c55e',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{stockPct(item)}%</div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: low ? '#ef4444' : '#1e293b', fontSize: 15 }}>
                      {item.quantity}
                    </span>
                  </td>
                  <td style={{ color: '#64748b' }}>{item.unit}</td>
                  <td style={{ color: '#64748b' }}>{item.low_stock_alert}</td>
                  <td>
                    {low
                      ? <span className="badge badge-ended">⚠ Low Stock</span>
                      : <span className="badge badge-active">✓ OK</span>
                    }
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-success btn-sm" onClick={() => openRestock(item)} title="Restock">
                        <PackagePlus size={13}/> Restock
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(item)} title="Edit">
                        <Pencil size={13}/>
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteItem(item)} title="Delete">
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>
                  {search ? 'No products match your search' : 'No inventory items yet — click "Add Product"'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? 'Add Product' : 'Edit Product'} onClose={() => setModal(null)}>
          <div className="form-group">
            <label>Product Name</label>
            <input
              autoFocus
              value={form.product_name}
              onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
              placeholder="e.g. Cold Drinks"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <input type="number" min="0"
                value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="form-group">
              <label>Unit</label>
              <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Low Stock Alert — highlight row when qty drops to or below this</label>
            <input type="number" min="0"
              value={form.low_stock_alert}
              onChange={e => setForm(f => ({ ...f, low_stock_alert: parseInt(e.target.value) || 0 }))}
            />
            {form.quantity > 0 && (
              <div style={{ fontSize: 12, marginTop: 4, color: isLow(form) ? '#ef4444' : '#22c55e' }}>
                {isLow(form) ? '⚠ Would show as Low Stock with these values' : '✓ Would show as OK with these values'}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </Modal>
      )}

      {/* ── Restock Modal ── */}
      {modal === 'restock' && (
        <Modal title={`Restock — ${selected?.product_name}`} onClose={() => setModal(null)}>
          <div className="restock-info">
            <div className="restock-stat">
              <span>Current Stock</span>
              <strong style={{ color: isLow(selected) ? '#ef4444' : '#1e293b' }}>
                {selected?.quantity} {selected?.unit}
              </strong>
            </div>
            <div className="restock-stat">
              <span>Alert Below</span>
              <strong>{selected?.low_stock_alert} {selected?.unit}</strong>
            </div>
            <div className="restock-stat">
              <span>Status</span>
              <strong style={{ color: isLow(selected) ? '#ef4444' : '#22c55e' }}>
                {isLow(selected) ? '⚠ Low' : '✓ OK'}
              </strong>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Quantity to Add</label>
            <input
              type="number" min="1" autoFocus
              value={restock}
              onChange={e => setRestock(e.target.value)}
              placeholder="e.g. 24"
            />
            {parseInt(restock) > 0 && (
              <div style={{ fontSize: 13, marginTop: 6, padding: '6px 12px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0', color: '#15803d' }}>
                New stock will be: <strong>{(parseInt(selected?.quantity) || 0) + parseInt(restock)} {selected?.unit}</strong>
                {isLow({ quantity: (parseInt(selected?.quantity) || 0) + parseInt(restock), low_stock_alert: selected?.low_stock_alert })
                  ? ' — ⚠ still low'
                  : ' — ✓ will be OK'
                }
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-success" onClick={doRestock}><PackagePlus size={14}/> Add Stock</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
